import { DIMENSION_TEMPLATE, EMPTY_PIECE, Person, PERSON_TEMPLATE, Piece, TransportMovement, TRANSPORT_MOVEMENT_TEMPLATE, Value } from '../interfaces';
import { MovementTimes } from '../interfaces/movement-times';
import { UserService } from '../services/user.service';
import { Wrapper } from './wrapper';

export class PieceWrapper extends Wrapper {
  private piece = EMPTY_PIECE;

  private readonly FLIGHT_CODE = '4';
  private readonly FORWARD_CODE = '3';
  private readonly FORWARD_CUSTOM_CODE = '109';
  private readonly CHA_CUSTOM_CODE = '209';
  private readonly GHA_XCODE = '309';

  private readonly INBOUND = 'Inbound';
  private readonly OUTBOUND = 'Outbound';
  private readonly ACTUAL = 'Actual';
  private readonly PLANNED = 'Planned';

  private readonly LAT_OFFSET = 1000 * 3600 * 6; // 6h

  private readonly OUR_LOCATION = 'FRA';

  routingTransports: TransportMovement[] = [];

  constructor(piece: Piece, userService: UserService) {
    super(piece, piece?.dimensions || DIMENSION_TEMPLATE, userService);
    if (piece) {
      this.piece = piece;
      this.sortMovements();
      this.routingTransports = this.filterRoutingTransports();
    }
  }

  private filterRoutingTransports(): TransportMovement[] {
    let res: TransportMovement[] = [];
    let select = false;
    res = this.piece.transportMovements.filter(m => {
      select = select || (m.departureLocation.locationCode === this.OUR_LOCATION);
      return select && (m.modeCode === this.FLIGHT_CODE || m.modeCode === this.FORWARD_CODE);
    });
    return res;
  }

  private sortMovements() {
    let res: TransportMovement[] = this.piece.transportMovements;

    res.sort((a, b) => {
      const ta = a.movementTimes[0].movementTimestamp;
      const tb = b.movementTimes[0].movementTimestamp;
      if (ta < tb) {
        return -1;
      }
      if (tb < ta) {
        return 1;
      }
      return 0;
    });
  }

  firstOf(modeCode: string): TransportMovement | undefined {
    return this.piece.transportMovements.find((m) => m.modeCode === modeCode);
  }

  firstFlight(): TransportMovement | undefined {
    return this.firstOf(this.FLIGHT_CODE);
  }

  firstMovement(): TransportMovement | undefined {
    return this.piece.transportMovements.find((m) => m.modeCode === this.FLIGHT_CODE || m.modeCode === this.FORWARD_CODE);
  }

  ghaMovement(): TransportMovement | undefined {
    return this.piece.transportMovements.find((m) =>
      m.modeCode === this.GHA_XCODE /* && m.departureLocation.locationCode == this.screenLocation */
    );
  }

  firstGhaMovement(): TransportMovement | undefined {
    return this.firstOf(this.GHA_XCODE);
  }

  getTime(movementTimes?: MovementTimes[], direction?: string, timeType?: string, offset?: number): string {
    let res = '';
    if (movementTimes) {
      const t = movementTimes.find(m => m.direction === direction && m.timeType == timeType);
      if (t) {
        if (offset) {
          res = new Date(Date.parse(t.movementTimestamp) - offset).toISOString();
        } else {
          res = t.movementTimestamp;
        }
      }
    }
    return this.formatDate(res);
  }

  get carrier(): string {
    const flight = this.firstFlight();
    return flight?.transportMeans?.transportCompany?.companyName ?? '';
  }

  get destination(): string {
    return this.firstFlight()?.arrivalLocation?.locationName ?? '';
  }

  get departure(): string {
    return this.firstFlight()?.departureLocation?.locationName ?? '';
  }

  get flightNo(): string {
    return this.firstFlight()?.transportIdentifier ?? '';
  }

  get shipmentId(): string {
    return this.piece.shipment['@id'];
  }

  get commodity(): string {
    return this.piece.goodsDescription;
  }

  get weight(): string {
    return this.valueString(this.piece.grossWeight);
  }

  get spxStatus(): string {
    return this.piece.securityDeclaration.securityStatus;
  }

  get flightArrivalTime(): string {
    return this.getTime(this.firstFlight()?.movementTimes, this.INBOUND, this.PLANNED);
  }

  get flightArrival(): string {
    return this.firstFlight()?.arrivalLocation.locationCode ?? '';
  }

  get nextArrival(): string {
    return this.firstGhaMovement()?.arrivalLocation.locationCode ?? '';
  }

  get flightDepartureTime(): string {
    return this.getTime(this.firstFlight()?.movementTimes, this.OUTBOUND, this.PLANNED);
  }

  get flightDeparture(): string {
    return this.firstFlight()?.departureLocation.locationCode ?? '';
  }

  get handlingInstructions(): string {
    return this.piece.handlingInstructions?.[0].serviceDescription ?? '';
  }

  get uldSerial(): string {
    return this.piece?.uldReference?.serialNumber ?? '';
  }

  get chargeableWeight(): string {
    return this.valueString(this.piece.volumetricWeight?.chargeableWeight);
  }

  get cha(): string {
    return this.ghaMovement()?.departureLocation.locationName ?? '';
  }

  get gha(): string {
    return this.firstGhaMovement()?.transportMeans.transportCompany.companyName ?? '';
  }

  get pickupAddress(): string {
    return this.ghaMovement()?.departureLocation.address.street ?? '';
  }

  get deliveryAddress(): string {
    return this.ghaMovement()?.arrivalLocation.address.street ?? '';
  }

  get positioningTime(): string {
    return this.getTime(this.firstFlight()?.movementTimes, this.OUTBOUND, this.PLANNED);
  }

  get forwarderPickupAddress(): string {
    let res = '';
    const loc = this.firstOf(this.FORWARD_CODE)?.departureLocation;
    if (loc) {
      res = `${loc.locationName}\n${loc.address.street}\n${loc.address.postalCode}\n${loc.address.cityName}\n${loc.address.country.name}`;
    }
    return res;
  }

  get forwarderDeliveryAddress(): string {
    let res = '';
    const loc = this.firstOf(this.FORWARD_CODE)?.arrivalLocation;
    if (loc) {
      res = `${loc.locationName}\n${loc.address.street}\n${loc.address.postalCode}\n${loc.address.cityName}\n${loc.address.country.name}`;
    }
    return res;
  }

  private getOperator(): Person {
    const movement = this.firstOf(this.FORWARD_CODE) || TRANSPORT_MOVEMENT_TEMPLATE;
    return movement.transportMeansOperators?.[0] ?? PERSON_TEMPLATE; // TODO handle more than one operator
  }

  get driverName(): string {
    const transportOp = this.getOperator();
    return `${transportOp.salutation} ${transportOp.firstName} ${transportOp.lastName}`;
  }

  get driverId(): string {
    return this.getOperator().employeeId;
  }

  get truckId(): string {
    const movement = this.firstOf(this.FORWARD_CODE) || TRANSPORT_MOVEMENT_TEMPLATE;
    return movement.transportMeans?.vehicleRegistration || ''
  }

  get truckCompany(): string {
    const movement = this.firstOf(this.FORWARD_CODE) || TRANSPORT_MOVEMENT_TEMPLATE;
    return movement.transportMeans?.transportCompany.companyName || '';
  }

  get otherIdentifier(): string {
    return this.piece.otherIdentifiers.map(e => e.name).join(';');
  }

  get lat(): string {
    return this.getTime(this.firstFlight()?.movementTimes, this.OUTBOUND, this.PLANNED, this.LAT_OFFSET);
  }


  get storageLocation(): string {
    let res = '';
    const movements = this.piece.transportMovements
    const code = this.customCode();
    const movement = movements.find(m => m.modeCode === code && m.externalReferences.documentId);
    if (movement) {
      res = movement.arrivalLocation.address.street;
    }
    return res;
  }

  private isCha(): boolean {
    return this.userService.currentUser()?.username === 'CHA';
  }

  private customCode(): string {
    return this.isCha() ? this.CHA_CUSTOM_CODE : this.FORWARD_CUSTOM_CODE;
  }

  get warehouseReceipt(): string {
    let res = '';
    const movements = this.piece.transportMovements
    const code = this.customCode();
    const movement = movements.find(m => m.modeCode === code && m.externalReferences.documentId);
    if (movement) {
      res = movement.externalReferences.documentId;
    }
    return res;
  }


  get finalDestination(): string {
    let res = '';
    const movements = this.piece.transportMovements;
    if (movements?.length > 0) {
      const last = movements[movements.length - 1];
      res = last.arrivalLocation.locationCode;
    }

    return res;
  }

  carrierOf(movement: TransportMovement) {
    return movement?.transportMeans?.transportCompany?.companyName || '';
  }
}
